import type { BookingCancellationPolicyDisplay } from "./operations";

/**
 * "CHANGED", DEFINED (rc.10, Legal and Product): the policy type, the
 * headline, the body or the SERVED locale differ. Anything else (a cache
 * object's identity, a __typename) is not a change. Two absent policies are
 * the same; one absent and one present are not.
 */
export function samePolicy(
  a: BookingCancellationPolicyDisplay | null | undefined,
  b: BookingCancellationPolicyDisplay | null | undefined
): boolean {
  if (!a || !b) return !a && !b;
  return a.policyType === b.policyType && a.headline === b.headline && a.body === b.body && a.locale === b.locale;
}

/** A fresh read of the policy just before a send: what it says now, or that it could not be read. */
export type PolicyReread =
  | { readonly kind: "read"; readonly policy: BookingCancellationPolicyDisplay | null }
  | { readonly kind: "failed" };

/** What a send's pre-check found: go ahead, or stop and say why. */
export type PolicyCheck = "same" | "changed" | "unreadable";

export async function checkPolicy(
  reread: () => Promise<PolicyReread>,
  agreed: BookingCancellationPolicyDisplay | null | undefined
): Promise<PolicyCheck> {
  // A throw is a read that failed, never an escape: the send's latch is held
  // around this call, and an unwound check must stop the send, not wedge it.
  let now: PolicyReread;
  try {
    now = await reread();
  } catch {
    return "unreadable";
  }
  if (now.kind === "failed") return "unreadable";
  return samePolicy(now.policy, agreed) ? "same" : "changed";
}
